import { ISbStoryData } from 'storyblok-js-client';

import { locales, localesToString } from '@monoimin/constants';

import { getLocalePrefix } from './getLocale';

import type {
  AnyByLocales,
  ByLocales,
  Locales,
  MultilinkStoryblok,
} from '@monoimin/sb-types';

const templateMsgRegex = /\{(\w*)\}/g;
export const replaceTemplate = (
  templateMsg: string,
  replacers?: { [k: string]: string }
) => {
  if (!replacers) {
    return templateMsg;
  }
  return templateMsg.replace(templateMsgRegex, (_, key) => replacers[key]);
};

type StoryWithAlternate = Pick<
  ISbStoryData,
  'slug' | 'content' | 'full_slug' | 'lang'
>;

export const getStoryLocale = (story: StoryWithAlternate) => {
  const folderLocale = getFolderLocale(story.full_slug);

  if (!folderLocale) {
    return story.lang === 'default' ? 'en' : (story.lang as Locales);
  }

  return folderLocale;
};

const getSlugFromFullSlug = (fullSlug: string) => {
  const splittedFullSlug = fullSlug.split('/');
  return splittedFullSlug[splittedFullSlug.length - 1];
};
export const getStoryAlterSlugs = (story: StoryWithAlternate) => {
  const storyLocale = getStoryLocale(story);

  const content = story.content;

  const legacyAlterLocale = storyLocale === 'en' ? 'ru' : 'en';
  const legacyAlterKey = `alternate_${legacyAlterLocale}`;
  const legacyAlterUrl =
    content[legacyAlterKey]?.cached_url || content['alternate']?.cached_url;

  const legacyAlterSlug = legacyAlterUrl
    ? getSlugFromFullSlug(legacyAlterUrl)
    : '';

  const localesToSlugs = locales.reduce<{ [k in Locales]?: string }>(
    (acc, locale) => {
      const key = `alternate_${locale}`;
      const link = content[key] as MultilinkStoryblok | undefined;
      const slug = link && getSlugFromFullSlug(link.cached_url);

      return {
        ...acc,
        [locale]: slug,
      };
    },
    {}
  );

  return {
    ...localesToSlugs,
    [storyLocale]: story.slug,
    [legacyAlterLocale]: legacyAlterSlug,
  };
};

export const getStoryAlterPathnames = (
  story: StoryWithAlternate,
  folder?: string
) => {
  const slugsByLocales = getStoryAlterSlugs(story);

  const folderPrefix = folder ? `/${folder}` : ``;

  const pathnamesToLocales = locales.reduce((acc, locale) => {
    const slug = slugsByLocales[locale];
    if (!slug) return acc;

    const localePrefix = getLocalePrefix(locale);
    const pathname = `${folderPrefix}/${slug}`;
    return {
      ...acc,
      [locale]: localePrefix + pathname,
    };
  }, localesToString);

  return pathnamesToLocales;
};

export const storyHasAlternates = (pathnamesToLocales: ByLocales<string>) => {
  return Object.values(pathnamesToLocales).filter(Boolean).length > 1;
};

export function itemByLocales<T>(item: T) {
  return locales.reduce<{ [k in Locales]: T }>(
    (acc, locale) => ({ ...acc, [locale]: item }),
    {} as AnyByLocales
  );
}

export function functionResultByLocales<T>(callback: (locale: Locales) => T) {
  return locales.reduce<{ [k in Locales]: T }>(
    (acc, locale) => ({ ...acc, [locale]: callback(locale) }),
    {} as AnyByLocales
  );
}

export async function promiseResultByLocales<T>(
  callback: (locale: Locales) => Promise<T>
) {
  if (!Array.isArray(locales) || locales.length === 0) {
    return {} as Record<Locales, T | Error>;
  }

  const promises = locales.map((locale) => {
    if (!locale) {
      return Promise.resolve(null);
    }
    return callback(locale);
  });

  const results = await Promise.allSettled(promises);

  return locales.reduce((acc, locale, index) => {
    if (index >= results.length) {
      return acc;
    }

    const result = results[index];
    if (result.status === 'fulfilled') {
      // Only include non-null successful values
      if (result.value !== null) {
        acc[locale] = result.value;
      }
    } else {
      acc[locale] = result.reason;
    }
    return acc;
  }, {} as Record<Locales, T | Error>);
}

export const getFolderLocale = (fullSlug: string) => {
  const splittedFullSlug = fullSlug.split('/');
  const folderLocale = splittedFullSlug.find((s) =>
    locales.includes(s as Locales)
  );

  return folderLocale;
};
